<template>
  <action-dispatcher action="product/getProduct" :parameters="$route.params.id">
    <div class="view-member-area-modules-add-quizz">
      <div class="add-quizz-header">
        <snow-steps v-model="currentStep" :steps="steps" />
        <div class="d-flex gap-2">
          <snow-button
            :text="currentStep === 1 ? 'Cancelar' : 'Voltar'"
            variant="gray"
            @click="currentStep === 1 ? cancel() : previous()"
          />
          <snow-button
            :text="currentStep === steps.length ? 'Salvar' : 'Próximo'"
            @click="currentStep === steps.length ? save() : next()"
            :disabled="!isValid"
          />
        </div>
      </div>
      <div class="add-quizz-content">
        <form-quizz-basic-info
          v-if="currentStep === 1"
          :form="form"
          :errors="errors"
          :loading="loading"
        />
        <form-quizz-questions
          v-if="currentStep === 2"
          :form="form"
          v-model="form"
          :errors="errors"
          :loading="loading"
          @update="dispatch"
          @delete-question="submitDelete"
        />
      </div>
    </div>
  </action-dispatcher>
</template>

<script>
import FormQuizzBasicInfo from "@/components/product/details/content/modules/quizz/FormQuizzBasicInfo";
import FormQuizzQuestions from "@/components/product/details/content/modules/quizz/FormQuizzQuestions";
import { TRANSLATE_FIELD_QUIZZ_MAP } from "@/constants/fields.js";
import { formFields, get_error_msg, removeTimeZone } from "@/functions/helpers";
import MemberAreaMixin from "@/mixins/memberArea.js";
import ProductActionsMixin from "@/mixins/productActions.js";
import moment from "moment";
import { mapState } from "vuex";

export default {
  mixins: [ProductActionsMixin, MemberAreaMixin],
  components: {
    FormQuizzQuestions,
    FormQuizzBasicInfo
  },
  mounted() {
    this.dispatch();
  },
  data() {
    return {
      ...formFields(
        [
          "id",
          "title",
          "description",
          "time_limit_minutes",
          "approval_average",
          "allow_reveal_answer_keys",
          "number_of_attempts",
          "release_at",
          "days_to_release",
          "module_id",
          "questions"
        ],
        {
          module_id: this.$route.params.module_id || null,
          questions: [],
          time_limit_minutes: 0,
          approval_average: 0,
          allow_reveal_answer_keys: false,
          number_of_attempts: 0
        }
      ),
      currentStep: 1,
      loading: false
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    steps() {
      return ["Informações básicas", "Questões"];
    },
    isValid() {
      if (this.currentStep === 1) {
        return (
          this.form.title &&
          this.form.title.length <= 64 &&
          this.form.description &&
          this.form.description.length <= 1000 &&
          (this.form.release_at
            ? moment(this.form.release_at, "YYYY-MM-DD", true).isValid()
            : true)
        );
      }

      return true;
    },
    question_without_right_answer() {
      if (this.form.questions.length) {
        return this.form.questions.findIndex(
          q =>
            !q.answer_options
              .filter(a => !a.deleted)
              .reduce((acc, curr) => acc || curr.is_right_answer, false)
        );
      }

      return -1;
    },
    question_without_title() {
      if (this.form.questions.length) {
        return this.form.questions.findIndex(q =>
          q.answer_options
            .filter(a => !a.deleted)
            .reduce((acc, curr) => acc || `${curr.title}`.length == 0, false)
        );
      }

      return -1;
    }
  },
  methods: {
    dispatch() {
      if (this.$route.params.quizz_id) {
        this.$store
          .dispatch("membersQuizzes/getQuizz", this.$route.params.quizz_id)
          .then(data => {
            const past_release_at = data.release_at
              ? {
                  release_at: moment(removeTimeZone(data.release_at)).format(
                    "YYYY-MM-DD"
                  )
                }
              : {};
            const number_of_attempts =
              data.number_of_attempts > 0
                ? Number(data.number_of_attempts) - 1
                : 0;
            this.form = {
              ...data,
              number_of_attempts,
              ...past_release_at
            };
          });
      }
    },
    changeStep(step) {
      this.currentStep = step;
    },
    isValidFields() {
      if (!this.form.title.length) {
        this.$message.warning(
          "Preencha o título da avaliação antes de salvar."
        );
        return false;
      }

      if (!this.form.description.length) {
        this.$message.warning(
          "Preencha as orientações para a avaliação antes de salvar."
        );
        return false;
      }

      if (this.form.description.length > 1000) {
        this.$message.warning(
          "A orientação para a avaliação deve ter até 1000 caractéres"
        );
        return false;
      }

      if (
        this.form.release_at &&
        !moment(this.form.release_at, "YYYY-MM-DD", true).isValid()
      ) {
        this.$message.warning(
          "Digite uma data válida, maior que hoje para liberação da avaliação em uma data específica"
        );
        return false;
      }

      if (this.currentStep === 2) {
        const nonDeleted = this.form.questions.filter(q => !q.deleted);

        if (!nonDeleted.length) {
          this.$message.warning(
            "Por favor, adicione pelo menos uma questão à avaliação"
          );
          return false;
        }

        const questionWithoutAlternatives = nonDeleted.findIndex(
          q =>
            !q.answer_options ||
            !q.answer_options.filter(a => !a.deleted).length
        );

        if (questionWithoutAlternatives > -1) {
          this.$message.warning(
            `A questão ${questionWithoutAlternatives +
              1} precisa ter pelo menos uma alternativa`
          );
          return false;
        }

        if (this.question_without_right_answer > -1) {
          this.$message.warning(
            `Por favor, indique a resposta correta da questão ${this
              .question_without_right_answer + 1}`
          );
          return false;
        }

        if (this.question_without_title > -1) {
          this.$message.warning(
            `Por favor, há pelo menos uma alternativa sem texto definido na questão: ${this
              .question_without_title + 1}`
          );
          return false;
        }
      }

      return true;
    },
    next() {
      if (!this.isValidFields()) return;

      this.changeStep(this.currentStep + 1);
    },
    previous() {
      this.changeStep(this.currentStep - 1);
    },
    cancel() {
      if (
        [
          "product-members-module-quizz-final-edit",
          "product-members-module-quizz-edit"
        ].includes(this.$route.name)
      ) {
        this.$router.push({
          name: "product-member-area-modules",
          params: { id: this.$route.params.id }
        });
      } else {
        this.$router.go(-1);
      }
    },
    build_payload(form) {
      const payload = { ...form };
      for (let k in payload.questions) {
        const alternatives = [...payload.questions[k].answer_options];
        for (let i in alternatives) {
          if (
            alternatives[i].id &&
            `${alternatives[i].id}`.indexOf("local-alt") > -1
          ) {
            delete alternatives[i].id;
          }
        }

        payload.questions[k].answer_options = alternatives;
      }

      let release_at = null;

      if (form.release_at) {
        release_at = moment(form.release_at, "YYYY-MM-DD").toISOString();
      }

      return { ...payload, release_at };
    },
    save() {
      if (this.loading) return;

      if (!this.isValidFields()) return;

      let payload = this.build_payload({
        ...this.form,
        number_of_attempts: Number(this.form.number_of_attempts) + 1,
        product_id: this.$route.params.id
      });

      let action = "membersQuizzes/post";
      if (payload.id) {
        action = "membersQuizzes/put";
      }

      this.loading = true;
      this.$store
        .dispatch(action, payload)
        .then(response => {
          this.$message.success("A avaliação foi salva com sucesso!");
          this.$router.push({
            name: "product-member-area-modules",
            params: {
              id: this.$route.params.id
            }
          });
        })
        .catch(e => {
          let msg = get_error_msg(
            e.response.data,
            TRANSLATE_FIELD_QUIZZ_MAP,
            `Erro ao salvar a avaliação`
          );
          this.$message.error(msg);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deleteQuestion() {
      this.loading = true;
      this.$store
        .dispatch("membersQuizzes/put", this.form)
        .then(response => {
          this.$message.success("A questão deletada foi deletada");
          this.dispatch();
        })
        .catch(e => {
          this.$message.error("Erro ao deletar a questão");
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.view-member-area-modules-add-quizz {
  .add-quizz-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 28px;

    @media screen and (max-width: 991px) {
      flex-direction: column;
      align-items: stretch;
      gap: 32px;

      button {
        width: 100%;
      }
    }
  }
  .add-quizz-content {
    background: var(--bg-2);
    border-radius: 16px;
    padding: 24px;
  }
}
</style>
